/**
 * Shared contract for the Visualizations & Plans feature (ZPL-134).
 *
 * A separate parallel stack to Inspirations: a project's renders / plans,
 * each either an uploaded file (image/pdf/video) or a URL. Same direct
 * Cloudinary upload + confirm flow — file bytes never transit the API. The
 * allow-lists + size caps below are mirrored client-side for early
 * rejection, but the server is authoritative (it pins the content type into
 * the signature and re-validates the real object at confirm time). The
 * private-asset registry (`files` table + `CloudinaryUploadTarget`) and the
 * `original`/`thumbnail` file roles are shared infra reused from Inspirations.
 */

import type { CloudinaryUploadTarget } from "./inspirations"

export const VISUALIZATION_KINDS = ["image", "pdf", "video", "url"] as const
export type VisualizationKind = (typeof VISUALIZATION_KINDS)[number]

/** File-backed kinds — everything except `url`. */
export const VISUALIZATION_FILE_KINDS = ["image", "pdf", "video"] as const
export type VisualizationFileKind = (typeof VISUALIZATION_FILE_KINDS)[number]

/**
 * Upload lifecycle. `pending_upload` rows are never surfaced to clients;
 * only `ready` rows are listed. `rejected` is terminal (confirm found the
 * object invalid and deleted it).
 */
export const VISUALIZATION_STATUSES = [
	"pending_upload",
	"ready",
	"rejected",
] as const
export type VisualizationStatus = (typeof VISUALIZATION_STATUSES)[number]

/**
 * Allowed upload content types per file kind. `image/svg+xml` and any
 * HTML/XML-ish type are deliberately EXCLUDED — served from the storage
 * origin they execute (stored-XSS). The server pins the chosen type into
 * the V4 signature and re-checks the real bytes at confirm; this list is the
 * client-side pre-filter only.
 */
export const VISUALIZATION_ALLOWED_CONTENT_TYPES: Record<
	VisualizationFileKind,
	readonly string[]
> = {
	image: ["image/png", "image/jpeg", "image/webp"],
	pdf: ["application/pdf"],
	video: ["video/mp4"],
} as const

/**
 * Per-kind maximum upload size in bytes. Authoritatively enforced at confirm
 * (stat + delete if over). Renders and plan PDFs run large, so the cap is
 * 25 MB — five times the Inspirations limit.
 */
export const VISUALIZATION_MAX_BYTES: Record<VisualizationFileKind, number> = {
	image: 25 * 1024 * 1024,
	pdf: 25 * 1024 * 1024,
	video: 25 * 1024 * 1024,
} as const

export const VISUALIZATION_LIMITS = {
	titleMax: 200,
	descriptionMax: 2000,
	/** Max non-deleted visualizations per project (anti-flood quota). */
	perProjectMax: 200,
} as const

/**
 * The file kind an allowed content type belongs to, or `undefined` if the
 * type is not on any allow-list (rejected). Shared so the web upload modal
 * derives `kind` from the picked file exactly as the server does.
 */
export function visualizationKindForContentType(
	contentType: string,
): VisualizationFileKind | undefined {
	for (const kind of VISUALIZATION_FILE_KINDS) {
		if (VISUALIZATION_ALLOWED_CONTENT_TYPES[kind].includes(contentType)) {
			return kind
		}
	}
	return undefined
}

/**
 * A visualization as returned by the API (JSON-serialized — Dates are ISO
 * strings). `thumbnailUrl` / `sourceUrl` are short-lived signed Cloudinary
 * URLs minted per request (never persisted); thumbnails are on-the-fly
 * transformations (image crop, PDF first page, video poster). `null` where
 * not applicable (e.g. a url-kind's `sourceUrl` is the external `url`).
 */
/**
 * Identity of whoever uploaded/created an asset (ZPL-265). The display name is
 * resolved server-side using the same convention as the property-journal actor
 * snapshot ("First Last", falling back to the email when no name is set). For
 * the AI actor `displayName` is null and the client renders the assistant label.
 */
export interface UploadedByDTO {
	actorType: "user" | "ai"
	displayName: string | null
}

export interface VisualizationDTO {
	id: string
	projectId: string
	kind: VisualizationKind
	status: VisualizationStatus
	title: string | null
	description: string | null
	/** External link for `url` kind; null otherwise. */
	url: string | null
	/** Signed Cloudinary thumbnail (image crop / PDF page / video poster); null for url until enriched. */
	thumbnailUrl: string | null
	/** Signed URL to the original object (file kinds); null for `url` kind. */
	sourceUrl: string | null
	/** Content type of the original object (file kinds); null for `url`. */
	mimeType: string | null
	createdAt: string
	/** Who uploaded/created this asset (ZPL-265). */
	uploadedBy: UploadedByDTO
	/** Whether the requesting user created this row (drives the delete affordance). */
	canDelete: boolean
}

/** Body for `POST .../projects/:projectId/visualizations` (file kind). */
export interface CreateFileVisualizationBody {
	/** Exact MIME of the file to upload; must be on an allow-list. */
	contentType: string
	/** Display-only original filename (sanitized; never used for the object key). */
	filename: string
	/** Client-reported byte size; advisory pre-check (confirm re-stats). */
	declaredSize: number
	title?: string
	description?: string
}

/** Response for the create-file call — the client uploads to Cloudinary next. */
export interface CreateFileVisualizationResponse {
	visualizationId: string
	upload: CloudinaryUploadTarget
	expiresAt: string
}

/** Body for `POST .../projects/:projectId/visualizations/url`. */
export interface CreateUrlVisualizationBody {
	url: string
	title?: string
	description?: string
}

/** Body for `PATCH .../visualizations/:id`. At least one field. */
export interface UpdateVisualizationBody {
	/** Empty string clears the value (stored as null). */
	title?: string
	/** Empty string clears the value (stored as null). */
	description?: string
}

export interface VisualizationResponse {
	visualization: VisualizationDTO
}

export interface VisualizationListResponse {
	visualizations: VisualizationDTO[]
}
